import { Trophy, House, Lightning, ArrowsClockwise, Clock, Barbell } from "@phosphor-icons/react";

/**
 * Read-only summary screen rendered after the user confirms "Termina workout".
 * Stand-alone screen (not a modal): the parent swaps it in place of the
 * active workout view.
 */
function ToneTheme(records, deltaTonnage) {
  if (records.length > 0) {
    return {
      Icon: Trophy,
      iconColor: "#FFB100",
      label: "NUOVO RECORD!",
      labelColor: "#FFB100",
      title: "Sessione da ricordare!",
    };
  }
  if (deltaTonnage >= 0) {
    return {
      Icon: Lightning,
      iconColor: "#007AFF",
      label: "ALLENAMENTO COMPLETATO",
      labelColor: "#007AFF",
      title: null, // filled with clientName by caller
    };
  }
  return {
    Icon: ArrowsClockwise,
    iconColor: "#FF3B30",
    label: "ALLENAMENTO COMPLETATO",
    labelColor: "#FF3B30",
    title: "Giornata no, capita.",
  };
}

function formatKg(n) {
  return `${Number(n || 0).toLocaleString("it-IT")} kg`;
}

export default function WorkoutSummaryScreen({ workout, clientName, summary, onHome }) {
  const theme = ToneTheme(summary.records, summary.deltaTonnage);
  const dynamicTitle = theme.title || (clientName ? `Ottimo lavoro, ${clientName}!` : "Ottimo lavoro!");
  const Icon = theme.Icon;

  return (
    <div
      className="fixed inset-0 z-[80] bg-[#0A0A0A] overflow-y-auto"
      role="dialog"
      aria-modal="true"
      data-testid="workout-summary-screen"
    >
      <div className="max-w-md mx-auto px-4 py-6 space-y-5">
        {/* HERO */}
        <header className="text-center space-y-3 pt-3">
          <div
            className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center"
            style={{
              backgroundColor: `${theme.iconColor}1F`,
              border: `1px solid ${theme.iconColor}55`,
              boxShadow: `0 0 36px ${theme.iconColor}40`,
            }}
          >
            <Icon size={40} weight="fill" style={{ color: theme.iconColor }} />
          </div>
          <p
            className="text-[10px] font-bold uppercase tracking-[0.22em]"
            style={{ color: theme.labelColor }}
            data-testid="summary-tone-label"
          >
            {theme.label}
          </p>
          <h1
            className="text-3xl font-black tracking-tight text-white"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
            data-testid="summary-title"
          >
            {dynamicTitle}
          </h1>
          <p className="text-xs text-zinc-400">
            {summary.durationMinutes != null && <>Completato in <span className="text-white font-semibold">{summary.durationMinutes} min</span> · </>}
            <span className="text-white font-semibold">{summary.exerciseCount}</span> esercizi
          </p>
        </header>

        {/* METRICHE */}
        <div className="grid grid-cols-2 gap-3">
          <div
            className="bg-[#0F0F0F] border border-[#1F1F1F] rounded-2xl p-4"
            data-testid="summary-card-tonnage"
          >
            <div className="flex items-center gap-1.5 mb-2">
              <Barbell size={12} className="text-zinc-500" />
              <p className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold">Tonnellaggio</p>
            </div>
            <p
              className="text-2xl font-black tracking-tight text-white tabular-nums"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}
            >
              {formatKg(summary.totalTonnage)}
            </p>
            {summary.previousTonnage > 0 ? (
              <p
                className={`text-[11px] font-semibold mt-1 ${summary.deltaTonnage >= 0 ? "text-[#34C759]" : "text-[#FF3B30]"}`}
                data-testid="summary-tonnage-delta"
              >
                {summary.deltaTonnage >= 0 ? "+" : ""}{summary.deltaTonnage} kg <span className="text-zinc-500 font-normal">vs sess. prec.</span>
              </p>
            ) : (
              <p className="text-[11px] text-zinc-600 mt-1">Prima sessione</p>
            )}
          </div>
          <div
            className="bg-[#0F0F0F] border border-[#1F1F1F] rounded-2xl p-4"
            data-testid="summary-card-duration"
          >
            <div className="flex items-center gap-1.5 mb-2">
              <Clock size={12} className="text-zinc-500" />
              <p className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold">Durata</p>
            </div>
            <p
              className="text-2xl font-black tracking-tight text-white tabular-nums"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}
            >
              {summary.durationMinutes != null ? `${summary.durationMinutes} min` : "—"}
            </p>
            {summary.previousDurationMinutes != null ? (
              <p className="text-[11px] text-zinc-500 mt-1">
                Precedente: <span className="text-white font-semibold">{summary.previousDurationMinutes} min</span>
              </p>
            ) : (
              <p className="text-[11px] text-zinc-600 mt-1">—</p>
            )}
          </div>
        </div>

        {/* RECORDS (solo se ce ne sono) */}
        {summary.records.length > 0 && (
          <section
            className="bg-gradient-to-br from-[#FFB100]/15 to-[#FFB100]/5 border border-[#FFB100]/40 rounded-2xl p-4 space-y-3"
            data-testid="summary-records"
          >
            <div className="flex items-center gap-2">
              <Trophy size={16} weight="fill" className="text-[#FFB100]" />
              <h2
                className="text-base font-bold tracking-tight text-white uppercase"
                style={{ fontFamily: "Barlow Condensed, sans-serif" }}
              >
                Record battuti questa sessione
              </h2>
            </div>
            <ul className="space-y-2">
              {summary.records.map((r) => (
                <li
                  key={`pr-${r.name}`}
                  className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-[#0F0F0F] border border-[#FFB100]/20"
                  data-testid={`summary-record-${r.name}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-white truncate">{r.name}</p>
                    <p className="text-[11px] text-zinc-400">
                      {r.now.reps} reps × {r.now.weight_kg}kg
                      {r.previous && (
                        <> · <span className="text-zinc-500">prima: {r.previous.reps}×{r.previous.weight_kg}kg</span></>
                      )}
                    </p>
                  </div>
                  <span className="text-[9px] px-2 py-1 rounded-full bg-[#FFB100]/20 text-[#FFB100] font-bold uppercase tracking-wider whitespace-nowrap">
                    Nuovo massimale
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* DETTAGLIO ESERCIZI */}
        <section
          className="bg-[#0F0F0F] border border-[#1F1F1F] rounded-2xl divide-y divide-[#1A1A1A]"
          data-testid="summary-exercise-details"
        >
          <header className="px-4 py-3">
            <h2 className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Esercizi completati</h2>
          </header>
          {summary.exerciseRows.map((row) => (
            <div
              key={`row-${row.name}`}
              className="px-4 py-3 flex items-center gap-3"
              data-testid={`summary-exercise-${row.name}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white truncate">{row.name}</p>
                <p className="text-[11px] text-zinc-500">
                  {row.sets} serie × {row.repsLabel} reps · <span className="text-zinc-400">{formatKg(row.tonnage)}</span>
                </p>
              </div>
              {row.isPR && (
                <Trophy size={14} weight="fill" className="text-[#FFB100] flex-shrink-0" aria-label="Nuovo record" />
              )}
            </div>
          ))}
          {summary.exerciseRows.length === 0 && (
            <p className="px-4 py-6 text-center text-xs text-zinc-500 italic">
              Nessun esercizio completato in questa sessione.
            </p>
          )}
        </section>

        {/* AZIONE */}
        <div className="pt-2 pb-4">
          <button
            type="button"
            onClick={onHome}
            className="w-full h-12 rounded-xl bg-[#007AFF] hover:bg-[#3395FF] text-white text-sm font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform"
            data-testid="summary-home-button"
          >
            <House size={16} weight="fill" /> Torna alla home
          </button>
        </div>

        <p className="text-center text-[11px] text-zinc-600 pb-3">
          {workout?.title}
        </p>
      </div>
    </div>
  );
}
